import { useState, useRef, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { UploadCloud, File as FileIcon, Lock, X } from "lucide-react";
import api from "@/fileshare/lib/api";
import { formatBytes, formatApiErrorDetail } from "@/fileshare/lib/format";
import { copyToClipboard } from "@/fileshare/lib/clipboard";
import { Button } from "@/fileshare/components/ui/button";
import { Input } from "@/fileshare/components/ui/input";
import { Label } from "@/fileshare/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/fileshare/components/ui/select";
import { Switch } from "@/fileshare/components/ui/switch";
import UploadResult from "./UploadResult";

const MAX_FILES = 20;

const FREE_EXPIRY = [
  { label: "1 hour", seconds: 3600 },
  { label: "3 hours", seconds: 10800 },
  { label: "6 hours", seconds: 21600 },
  { label: "12 hours", seconds: 43200 },
  { label: "1 day", seconds: 86400 },
];
const PLUS_EXPIRY = [
  { label: "1 day", seconds: 86400 },
  { label: "7 days", seconds: 604800 },
  { label: "30 days", seconds: 2592000 },
  { label: "90 days", seconds: 7776000 },
];
const BIZ_EXPIRY = [
  { label: "7 days", seconds: 604800 },
  { label: "30 days", seconds: 2592000 },
  { label: "90 days", seconds: 7776000 },
  { label: "Never expires", seconds: "never" },
];

function FileList({ files, onRemove }) {
  return (
    <div className="w-full space-y-1.5 max-h-44 overflow-y-auto pr-1" data-testid="selected-files-list" onClick={(e) => e.stopPropagation()}>
      {files.map((f, i) => (
        <div key={`${f.name}-${f.size}-${i}`} className="flex items-center gap-3 justify-between bg-white rounded-md border border-slate-100 px-3 py-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-8 w-8 rounded bg-blue-50 flex items-center justify-center shrink-0">
              <FileIcon className="h-4 w-4 text-blue-500" />
            </div>
            <div className="text-left min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{f.name}</p>
              <p className="text-xs text-slate-400">{formatBytes(f.size)}</p>
            </div>
          </div>
          <Button
            data-testid={`remove-file-${i}`}
            variant="ghost"
            size="icon"
            onClick={() => onRemove(i)}
            className="text-slate-400 hover:text-red-500 h-7 w-7 shrink-0"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
    </div>
  );
}

function DropZone({ files, dragOver, inputRef, onDrop, onDragOver, onDragLeave, onSelect, onRemove }) {
  return (
    <div
      data-testid="upload-zone"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      className={`cursor-pointer border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center text-center transition-colors duration-200 ${
        dragOver ? "border-blue-500 bg-blue-50/50" : "border-slate-200 bg-slate-50/60 hover:border-slate-300"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        data-testid="file-input"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) onSelect(Array.from(e.target.files));
          e.target.value = "";
        }}
      />
      {files.length > 0 ? (
        <>
          <FileList files={files} onRemove={onRemove} />
          <p className="text-xs text-slate-400 mt-3">
            {files.length}/{MAX_FILES} files · {formatBytes(files.reduce((a, f) => a + f.size, 0))} · click or drop to add more
          </p>
        </>
      ) : (
        <div className="py-4">
          <UploadCloud className="h-11 w-11 text-slate-400 mb-3 mx-auto" strokeWidth={1.5} />
          <p className="text-sm font-medium text-slate-700">Drag & drop files here</p>
          <p className="text-xs text-slate-500 mt-1">or click to browse · up to {MAX_FILES} files per transfer</p>
        </div>
      )}
    </div>
  );
}

export default function UploadCard({ tier }) {
  const expiryOptions = tier === "business" ? BIZ_EXPIRY : tier === "plus" ? PLUS_EXPIRY : FREE_EXPIRY;
  const canCustomSlug = tier === "plus" || tier === "business";

  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [expiry, setExpiry] = useState(String(expiryOptions[0].seconds));
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [customSlug, setCustomSlug] = useState("");
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  const shareUrl = useMemo(
    () => (result ? `${window.location.origin}/fileshares/d/${result.slug}` : ""),
    [result]
  );

  const addFiles = useCallback((incoming) => {
    setFiles((prev) => {
      const merged = [...prev];
      for (const f of incoming) {
        const dup = merged.some((m) => m.name === f.name && m.size === f.size);
        if (!dup) merged.push(f);
      }
      if (merged.length > MAX_FILES) {
        toast.error(`Maximum ${MAX_FILES} files per transfer`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });
  }, []);

  const removeFile = useCallback((index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setProgress(0);
    try {
      // 1) Ask backend for presigned R2 upload URLs.
      const initBody = {
        files: files.map((f) => ({
          filename: f.name,
          size: f.size,
          content_type: f.type || "application/octet-stream",
        })),
        expiry_seconds: expiry,
      };
      if (usePassword && password) initBody.password = password;
      if (canCustomSlug && customSlug.trim()) initBody.custom_slug = customSlug.trim();

      const { data: init } = await api.post("/batches/init-upload", initBody);
      const { batch_id, files: entries } = init;

      // 2) Upload each file directly to R2 via presigned PUT, tracking
      //    aggregate progress across all files.
      const sizes = files.map((f) => f.size);
      const totalBytes = sizes.reduce((a, b) => a + b, 0) || 1;
      const loaded = new Array(files.length).fill(0);
      const emitProgress = () => {
        const done = loaded.reduce((a, b) => a + b, 0);
        setProgress(Math.min(99, Math.round((done / totalBytes) * 100)));
      };

      const putOne = (file, entry, i) =>
        new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open(entry.method || "PUT", entry.upload_url, true);
          const headers = entry.required_headers || {};
          Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              loaded[i] = e.loaded;
              emitProgress();
            }
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              loaded[i] = sizes[i];
              emitProgress();
              resolve();
            } else {
              reject(new Error(`R2 upload failed (${xhr.status})`));
            }
          };
          xhr.onerror = () => reject(new Error("Network error during upload"));
          xhr.send(file);
        });

      await Promise.all(files.map((f, i) => putOne(f, entries[i], i)));

      // 3) Finalize — backend HEADs each object in R2 then activates the batch.
      const { data: finalized } = await api.post(`/batches/${batch_id}/finalize`);
      setProgress(100);
      setResult(finalized);
      toast.success("Files uploaded — your link is ready!");
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message || "Upload failed";
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const copyLink = async () => {
    const ok = await copyToClipboard(shareUrl);
    if (ok) {
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error("Could not copy — copy the link manually");
    }
  };

  const reset = () => {
    setFiles([]);
    setResult(null);
    setPassword("");
    setUsePassword(false);
    setCustomSlug("");
    setProgress(0);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-[0_2px_12px_rgba(0,0,0,0.04)] p-6 sm:p-8">
      <AnimatePresence mode="wait">
        {result ? (
          <motion.div
            key="result"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <UploadResult result={result} shareUrl={shareUrl} copied={copied} onCopy={copyLink} onReset={reset} />
          </motion.div>
        ) : (
          <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <DropZone
              files={files}
              dragOver={dragOver}
              inputRef={inputRef}
              onDrop={onDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onSelect={addFiles}
              onRemove={removeFile}
            />

            {/* settings */}
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-slate-700">Expires after</Label>
                <Select value={expiry} onValueChange={setExpiry}>
                  <SelectTrigger data-testid="expiry-select" className="mt-1.5 bg-slate-50/60 border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {expiryOptions.map((o) => (
                      <SelectItem key={String(o.seconds)} value={String(o.seconds)} data-testid={`expiry-option-${o.seconds}`}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {canCustomSlug && (
                <div>
                  <Label className="text-sm text-slate-700">Custom link slug</Label>
                  <Input
                    data-testid="custom-slug-input"
                    value={customSlug}
                    onChange={(e) => setCustomSlug(e.target.value)}
                    placeholder="my-files"
                    className="mt-1.5 bg-slate-50/60 border-slate-200"
                  />
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center justify-between rounded-md border border-slate-200 px-4 py-3 bg-slate-50/40">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-slate-400" />
                <Label className="text-sm text-slate-700">Password protect</Label>
              </div>
              <Switch data-testid="password-toggle" checked={usePassword} onCheckedChange={setUsePassword} />
            </div>
            <AnimatePresence>
              {usePassword && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                  <Input
                    data-testid="password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Set a password"
                    className="mt-3 bg-slate-50/60 border-slate-200"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {uploading && (
              <div className="mt-5">
                <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                  <span>Uploading {files.length} {files.length === 1 ? "file" : "files"}…</span><span>{progress}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <motion.div
                    className="h-full bg-blue-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ ease: "linear", duration: 0.2 }}
                    data-testid="upload-progress-bar"
                  />
                </div>
              </div>
            )}

            <motion.div whileHover={{ scale: files.length && !uploading ? 1.01 : 1 }} whileTap={{ scale: 0.99 }}>
              <Button
                data-testid="upload-submit-button"
                onClick={handleUpload}
                disabled={files.length === 0 || uploading}
                className="mt-6 w-full bg-blue-500 hover:bg-blue-600 text-white h-11 text-[15px] font-medium"
              >
                {uploading ? "Uploading…" : files.length > 1 ? `Upload ${files.length} files & Get Link` : "Upload & Get Link"}
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
